<template>
    <div class="container form-container p-4">
        <div class="text-3xl mb-4 pb-2 font-light flex items-center justify-between">
            <div data-cy="user-title">{{ $t('user.title') }}</div>
            <div class="flex flex-col">
                <a-checkbox
                    @change="onCheckboxChange"
                    v-model:checked="active_users_only"
                    data-cy="exclude-inactive-users"
                >
                    {{ $t('user.inactive_check') }}
                </a-checkbox>
                <a-dropdown :trigger="['click']" class="shadow-none" data-cy="user-import-export-dropdown">
                    <a-button style="margin-left: 8px" v-if="!is_association_section && is_admin_section">
                        {{ $t('user.import-export-select.title') }}
                        <DownOutlined />
                    </a-button>
                    <a-button style="margin-left: 8px" v-else>
                        {{ $t('user.import-export-select.import_title') }}
                        <DownOutlined />
                    </a-button>
                    <template #overlay>
                        <a-menu @click="onSelectClick">
                            <a-menu-item
                                v-if="!is_association_section && is_admin_section"
                                data-cy="user-export-select"
                                key="ExportAdmin"
                            >
                                <DownloadOutlined />
                                {{ $t('user.import-export-select.export') }}
                            </a-menu-item>
                            <a-menu-item data-cy="user-import-select" key="Import">
                                <UploadOutlined />
                                {{ $t('user.import-export-select.upload') }}
                            </a-menu-item>
                        </a-menu>
                    </template>
                </a-dropdown>
            </div>
        </div>
        <users-table
            ref="UserTable"
            :company_id="company_id"
            :association_id="association_id"
            :is_association_section="is_association_section"
            :is_admin_section="is_admin_section"
            :selected_row_keys="selected_row_keys"
            @onSelectChange="onSelectChange"
            @onTableAction="onTableAction"
            @onActionButtons="onActionButtons"
            :active_users_only="active_users_only"
        />
        <a-drawer
            data-cy="user-drawer"
            title="User Details"
            :width="500"
            :open="drawer_visible"
            :body-style="{ padding: '0px', paddingBottom: '10px' }"
            :destroyOnClose="true"
            :maskClosable="false"
            :rootClassName="'epic-' + moduleStateLink"
            @close="onClose"
        >
            <form-user
                data-cy="user-form"
                :user="user"
                :is_association_section="is_association_section"
                :is_admin_section="is_admin_section"
                :processing="processing"
                :validation="validation"
                @is-form-dirty="isDrawerFormDirty"
                @formSubmit="onSave"
                @onDrawerClose="onClose"
                @updateRoles="updateRoles"
                @updateCompany="updateCompany"
                ref="UserForm"
            >
                <template #actions>
                    <div data-cy="actions-list">
                        <a-dropdown :trigger="['click']" class="float-left" v-if="user.id">
                            <a-button @click="(e) => e.preventDefault()" data-cy="actions-list-button">
                                {{ $t('user.table.actions.form_actions') }}
                                <DownOutlined />
                            </a-button>
                            <template #overlay>
                                <a-menu @click="onActionButtonsForm">
                                    <a-menu-item
                                        v-if="!user.is_active"
                                        key="activate_user"
                                        data-cy="actions-activate-user"
                                    >
                                        {{ $t('user.table.actions.activate') }}
                                    </a-menu-item>
                                    <a-menu-item
                                        v-if="user.is_active"
                                        key="deactivate_user"
                                        data-cy="actions-deactivate-user"
                                    >
                                        {{ $t('user.table.actions.deactivate') }}
                                    </a-menu-item>
                                    <a-menu-divider />
                                    <a-menu-item key="change_password" data-cy="actions-change-password">
                                        {{ $t('user.table.actions.change_password') }}
                                    </a-menu-item>
                                    <a-menu-item key="reset_password" data-cy="actions-reset-password">
                                        {{ $t('user.table.actions.reset_password') }}
                                    </a-menu-item>
                                    <a-menu-item key="reset_attempt" data-cy="actions-reset-attempt">
                                        {{ $t('user.table.actions.reset_login') }}
                                    </a-menu-item>
                                </a-menu>
                            </template>
                        </a-dropdown>
                    </div>
                </template>
            </form-user>
        </a-drawer>
        <a-modal
            :title="change_password_modal_title"
            v-model:open="modal_visible"
            :destroyOnClose="true"
            :class="'modal epic-' + moduleStateLink"
        >
            <template #footer>
                <a-button key="back" @click="handleModalCancel" data-cy="button-cancel">
                    {{ $t('globals.buttons.cancel') }}
                </a-button>
                <a-button
                    key="submit"
                    type="primary"
                    :loading="processing"
                    @click="handleModalOk"
                    data-cy="button-save"
                >
                    {{ $t('globals.buttons.save') }}
                </a-button>
            </template>
            <form-user-password
                :user_password="user_password"
                :mode="mode"
                :processing="processing"
                :validation="validation"
                @is-form-dirty="isModalFormDirty"
                @formSubmit="onUserPasswordSave"
                ref="UserPasswordForm"
            />
        </a-modal>
        <a-modal
            :title="$t('user.forms.change_expiry_date.expiry_date')"
            v-model:open="date_modal_visible"
            :destroyOnClose="true"
            :class="'modal epic-' + moduleStateLink"
        >
            <template #footer>
                <a-button key="back" @click="handleDateModalCancel" data-cy="button-cancel">
                    {{ $t('globals.buttons.cancel') }}
                </a-button>
                <a-button
                    key="submit"
                    type="primary"
                    :loading="processing"
                    @click="handleDateModalOk"
                    data-cy="button-save"
                >
                    {{ $t('globals.buttons.save') }}
                </a-button>
            </template>
            <form-user-expiry-date
                :processing="processing"
                :validation="validation"
                :mode="mode"
                :user_expiry_date="user_expiry_date"
                @formSubmit="onExpiryDateSave"
                ref="UserExpiryDateForm"
            />
        </a-modal>
        <modal-export-data
            @formSubmit="onExport"
            :association_id="association_id"
            :processing="processing"
            :is_admin_section="is_admin_section"
            :is_association_section="is_association_section"
            ref="ExportDataModal"
        />
        <modal-import-data @formSubmit="onImport" :processing="processing" ref="ImportDataModal" />
        <modal-email-user
            @formSubmit="onActionButtons"
            ref="EmailUserModal"
            :email_data="email_data"
            @update="updateEmailData"
        />
    </div>
</template>

<script>
import UsersTable from '@/components/tables/Users.vue';
import FormUser from '@/components/forms/User.vue';
import FormUserExpiryDate from '@/components/forms/UserExpiryDate.vue';
import FormUserPassword from '@/components/forms/UserPassword.vue';
import UiCheckbox from '@/components/ui/Checkbox.vue';
import ModalExportData from '@/components/modals/UserExportData.vue';
import ModalImportData from '@/components/modals/UserImportData.vue';
import ModalEmailUser from '@/components/modals/EmailUserModal.vue';

import FileManager from '@/utils/file.js';
import { DownloadOutlined, DownOutlined, UploadOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        UsersTable,
        FormUser,
        FormUserExpiryDate,
        FormUserPassword,
        UiCheckbox,
        ModalExportData,
        ModalImportData,
        ModalEmailUser,
        DownloadOutlined,
        DownOutlined,
        UploadOutlined,
    },
    props: {
        company_id: {
            type: String,
            default: '',
        },
        association_id: {
            type: String,
            default: '',
        },
        is_admin_section: {
            type: Boolean,
            default: false,
        },
        is_association_section: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        moduleStateLink() {
            return this.$store.state.system.is_epicvault_enabled ? 'default' : 'claim';
        },
        isEpicVaultEnabled() {
            return this.$store.getters['system/isEpicvaultEnabled'];
        },
    },
    data() {
        return {
            user: {},
            user_password: {},
            user_expiry_date: {
                expiry_date: null,
            },
            email_data: {
                subject: '',
                body: '',
            },
            mode: '',
            drawer_visible: false,
            modal_visible: false,
            date_modal_visible: false,
            drawer_form_dirty: false,
            modal_form_dirty: false,
            validation: {
                errors: {},
                message: '',
            },
            processing: false,
            change_password_modal_title: '',
            selected_row_keys: [],
            active_users_only: true,
        };
    },
    methods: {
        openDrawer(id) {
            if (id != '') {
                this.$http.get('/api/users/' + id).then((response) => {
                    let userData = {
                        ...response.data.data,
                        expiry_date: this.$dayjs(response.data.data.expiry_date),
                        is_active: response.data.data.is_active ? true : false,
                        is_append_api_user_id_to_cover_link_id: response.data.data
                            .is_append_api_user_id_to_cover_link_id
                            ? true
                            : false,
                        is_multi_association_access: response.data.data.is_multi_association_access ? true : false,
                        access_association_ids:
                            response.data.data.access_association_ids === null ||
                            response.data.data.access_association_ids === ''
                                ? []
                                : _.split(response.data.data.access_association_ids, ','),
                        access_company_ids:
                            response.data.data.access_company_ids === null ||
                            response.data.data.access_company_ids === ''
                                ? []
                                : _.split(response.data.data.access_company_ids, ','),
                        is_tfa_enabled: response.data.data.is_tfa_enabled === 1 ? true : false,
                        is_association_tfa_enabled: response.data.data.is_association_tfa_enabled === 1 ? true : false,
                        is_company_tfa_enabled: response.data.data.is_company_tfa_enabled === 1 ? true : false,
                        enable_advanced_claims_analytics:
                            response.data.data.enable_advanced_claims_analytics === 1 ? true : false,
                        enable_advanced_cover_analytics:
                            response.data.data.enable_advanced_cover_analytics === 1 ? true : false,
                        is_module_claimsvault_enabled:
                            response.data.data.is_module_claimsvault_enabled === 1 ? true : false,
                        is_module_covervault_enabled:
                            response.data.data.is_module_covervault_enabled === 1 ? true : false,
                        is_data_provider_email: response.data.data.is_data_provider_email === 1 ? true : false,
                    };

                    this.user = userData;
                    this.validation = {
                        errors: {},
                        message: '',
                    };
                    this.drawer_visible = true;
                });
            } else {
                let expiry_date = null;

                if (this.company_id != '') {
                    this.$http.get('/api/companies/' + this.company_id + '/get-sec-info').then((response) => {
                        expiry_date = this.$dayjs(response.data.expiry_date);

                        this.user = {
                            company_id: this.company_id,
                            name: '',
                            default_landing_page: '',
                            email: '',
                            timezone: 'America/Jamaica',
                            locale: 'en',
                            roles: ['user'],
                            expiry_date: expiry_date,
                            default_expiry_date: expiry_date.startOf('day'),
                            is_active: true,
                            is_append_api_user_id_to_cover_link_id: false,
                            is_multi_association_access: false,
                            access_association_ids: [],
                            access_company_ids: [],
                            is_tfa_enabled: false,
                            is_company_tfa_enabled: response.data.is_company_tfa_enabled == 1 ? true : false,
                            is_association_tfa_enabled: response.data.is_association_tfa_enabled == 1 ? true : false,
                            enable_advanced_claims_analytics: true,
                            enable_advanced_cover_analytics: true,
                            is_module_claimsvault_enabled:
                                response.data.is_module_claimsvault_enabled == 1 ? true : false,
                            is_module_covervault_enabled:
                                response.data.is_module_covervault_enabled == 1 ? true : false,
                            can_edit_modules: response.data.can_edit_modules,
                        };

                        this.validation = {
                            errors: {},
                            message: '',
                        };

                        this.drawer_visible = true;
                    });
                } else {
                    this.user = {
                        company_id: '',
                        name: '',
                        default_landing_page: '',
                        email: '',
                        timezone: 'America/Jamaica',
                        locale: 'en',
                        roles: ['user'],
                        expiry_date: expiry_date,
                        default_expiry_date: expiry_date,
                        is_active: true,
                        is_append_api_user_id_to_cover_link_id: false,
                        is_multi_association_access: false,
                        access_association_ids: [],
                        access_company_ids: [],
                        is_tfa_enabled: false,
                        is_association_tfa_enabled: false,
                        is_company_tfa_enabled: false,
                        enable_advanced_claims_analytics: true,
                        enable_advanced_cover_analytics: true,
                        is_module_claimsvault_enabled: false,
                        is_module_covervault_enabled: false,
                        can_edit_modules: false,
                    };

                    this.validation = {
                        errors: {},
                        message: '',
                    };

                    this.drawer_visible = true;
                }
            }
        },
        updateRoles(checked_roles) {
            this.user.roles = checked_roles;
        },
        updateCompany(company_id) {
            this.$http.get('/api/companies/' + company_id + '/get-sec-info').then((response) => {
                let expiry_date = this.$dayjs(response.data.expiry_date);

                this.user = {
                    ...this.user,
                    expiry_date: expiry_date,
                    default_expiry_date: expiry_date.startOf('day'),
                    is_company_tfa_enabled: response.data.is_company_tfa_enabled == 1 ? true : false,
                    is_association_tfa_enabled: response.data.is_association_tfa_enabled == 1 ? true : false,
                    is_module_claimsvault_enabled: response.data.is_module_claimsvault_enabled == 1 ? true : false,
                    is_module_covervault_enabled: response.data.is_module_covervault_enabled == 1 ? true : false,
                    can_edit_modules: response.data.can_edit_modules,
                };
            });
        },
        isDrawerFormDirty(is_dirty) {
            this.drawer_form_dirty = is_dirty;
        },
        onSelectChange(selected_row_keys) {
            this.selected_row_keys = selected_row_keys;
        },
        onCheckboxChange(event) {
            const value = event.target.checked;

            if (value == true) {
                this.$refs.UserTable.fetch({
                    active_users_only: value,
                    search_value: this.$refs.UserTable.search_value,
                });
            } else {
                this.$refs.UserTable.fetch({
                    active_users_only: null,
                    search_value: this.$refs.UserTable.search_value,
                });
            }
        },
        onTableAction(data) {
            switch (data.key) {
                case 'create':
                    this.openDrawer('');
                    break;
                case 'edit':
                    this.openDrawer(data.id);
                    break;
                case 'activate':
                    this.activateUser('inline', data.id);
                    break;
                case 'deactivate':
                    this.deactivateUser('inline', data.id);
                    break;
                case 'change_password':
                    this.showChangePasswordModal('inline', data);
                    break;
                case 'reset_password':
                    this.resetPassword('inline', data.id);
                    break;
                case 'reset_attempts':
                    this.resetAttempt('inline', data.id);
                    break;
            }
        },
        onActionButtonsForm(event) {
            this.onActionButtons(event, 'form');
        },
        onActionButtons(event, type) {
            if (event.key == 'open_email_modal') {
                this.email_data = {
                    subject: '',
                    body: '',
                };
                this.$refs.EmailUserModal.showModal();
                return true;
            }

            if (this.$refs.UserTable.select_all_users) {
                if (event.key == 'reset_expiry_date') {
                    this.resetExpiryDateModalAll();
                    return true;
                }
                let params = {
                    ...this.$refs.UserTable.params,
                    action: event.key,
                    select_all_users: this.$refs.UserTable.select_all_users,
                    company_id:
                        this.company_id != ''
                            ? this.company_id
                            : this.$refs.UserTable.params.company_id
                              ? this.$refs.UserTable.params.company_id
                              : null,
                    association_id:
                        this.association_id != ''
                            ? this.association_id
                            : this.$refs.UserTable.params.association_id
                              ? this.$refs.UserTable.params.association_id
                              : null,
                    search_value: this.$refs.UserTable.params.search_value
                        ? this.$refs.UserTable.params.search_value
                        : null,
                    active_users_only: this.$refs.UserTable.params.active_users_only
                        ? this.$refs.UserTable.params.active_users_only
                        : null,
                };

                if (event.key == 'email_user') {
                    params = {
                        ...params,
                        ...event.data,
                    };
                }

                this.$http
                    .post('/api/users/action-all-users', params)
                    .then((response) => {
                        if (event.key == 'export_users') {
                            if (
                                _.includes(
                                    [
                                        'text/csv; charset=UTF-8',
                                        'text/csv; charset=utf-8',
                                        'text/plain; charset=UTF-8',
                                        'text/plain; charset=utf-8',
                                    ],
                                    response.headers['content-type'],
                                )
                            ) {
                                this.$notification.success({
                                    message: this.$t('user.notifications.success.export'),
                                });

                                FileManager.download(response);
                            } else {
                                this.$notification.error({ message: response.data.message });
                            }
                        } else {
                            if (response.data.status == 'success') {
                                this.$refs.UserTable.reload();

                                this.$notification.success({
                                    message: response.data.message,
                                });
                            } else {
                                this.$notification.error({
                                    message: response.data.message,
                                });
                            }
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVUR02' }),
                            });
                        }
                    });
            } else {
                switch (event.key) {
                    case 'activate_user':
                        this.activateUser(type);
                        break;
                    case 'deactivate_user':
                        this.deactivateUser(type);
                        break;
                    case 'change_password':
                        this.showChangePasswordModal(type);
                        break;
                    case 'reset_password':
                        this.resetPassword(type);
                        break;
                    case 'reset_attempt':
                        this.resetAttempt(type);
                        break;
                    case 'reset_expiry_date':
                        this.resetExpiryDateModal();
                        break;
                    case 'export_users':
                        this.onExport({
                            type: 'selection',
                        });
                        break;
                    case 'email_user':
                        this.emailUser(event.data);
                        break;
                }
            }
        },
        activateUser(type, user_id = '') {
            let data = {
                ids: [],
            };

            switch (type) {
                case 'default':
                    if (this.selected_row_keys.length == 1) {
                        data.id = this.selected_row_keys[0];
                    } else if (this.selected_row_keys.length > 1) {
                        data = {
                            ids: this.selected_row_keys,
                        };
                    }
                    break;
                case 'form':
                    if (this.$refs.UserForm.user.id !== undefined) {
                        data.ids = [this.$refs.UserForm.user.id];
                    }
                    break;
                case 'inline':
                    if (user_id !== '' && user_id !== undefined) {
                        data.ids = user_id;
                    }
                    break;
            }

            if (user_id !== '' && user_id !== undefined) {
                data.ids = [user_id];
            } else if (this.selected_row_keys.length >= 1) {
                data.ids = this.selected_row_keys;
            }

            if (data.ids.length >= 1) {
                this.$http
                    .post('/api/users/activate', data)
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.$refs.UserTable.reload();

                            this.$notification.success({
                                message: response.data.message,
                            });
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVUR01' }),
                            });
                        }
                    });
            } else {
                this.$notification.error({
                    message: this.$t('user.notifications.error.select_one_or_more'),
                });
            }
        },
        deactivateUser(type, user_id = '') {
            let data = {
                ids: [],
            };

            switch (type) {
                case 'default':
                    if (this.selected_row_keys.length == 1) {
                        data.id = this.selected_row_keys[0];
                    } else if (this.selected_row_keys.length > 1) {
                        data = {
                            ids: this.selected_row_keys,
                        };
                    }
                    break;
                case 'form':
                    if (this.$refs.UserForm.user.id !== undefined) {
                        data.ids = [this.$refs.UserForm.user.id];
                    }
                    break;
                case 'inline':
                    if (user_id !== '' && user_id !== undefined) {
                        data.ids = user_id;
                    }
                    break;
            }

            if (user_id !== '' && user_id !== undefined) {
                data.ids = [user_id];
            } else if (this.selected_row_keys.length >= 1) {
                data.ids = this.selected_row_keys;
            }

            if (data.ids.length >= 1) {
                this.$http
                    .post('/api/users/deactivate', data)
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.$refs.UserTable.reload();

                            this.$notification.success({
                                message: response.data.message,
                            });
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVUD02' }),
                            });
                        }
                    });
            } else {
                this.$notification.error({
                    message: this.$t('user.notifications.error.select_one_or_more'),
                });
            }
        },
        showChangePasswordModal(type, data = {}) {
            let id = undefined,
                name = '';

            switch (type) {
                case 'default':
                    if (this.selected_row_keys.length == 1) {
                        id = this.selected_row_keys[0];
                        let results = this.$refs.UserTable.data.filter((u) => u.id == id);

                        if (results.length == 1) {
                            name = results[0].name;
                        }
                    }
                    break;
                case 'form':
                    if (this.$refs.UserForm.user.id !== undefined) {
                        id = this.$refs.UserForm.user.id;
                        name = this.$refs.UserForm.user.name;
                    }
                case 'inline':
                    if (data.id !== '' && data.id !== undefined) {
                        id = data.id;
                        name = data.name;
                    }
                    break;
            }

            if (id == undefined) {
                this.$notification.error({
                    message: this.$t('user.notifications.error.select_one'),
                });
            } else {
                this.change_password_modal_title = this.$t('user.modals.change_password.title');

                if (name !== '') {
                    this.change_password_modal_title = this.$t('user.modals.change_password.title_user', {
                        user: name,
                    });
                }

                this.user_password = {
                    user_id: id,
                    password: '',
                    password_confirmation: '',
                };

                this.modal_visible = true;
            }
        },
        resetPassword(type, user_id = '') {
            let data = {
                id: '',
            };

            switch (type) {
                case 'default':
                    if (this.selected_row_keys.length == 1) {
                        data.id = this.selected_row_keys[0];
                    } else if (this.selected_row_keys.length > 1) {
                        data = {
                            ids: this.selected_row_keys,
                        };
                    }
                    break;
                case 'form':
                    if (this.$refs.UserForm.user.id !== undefined) {
                        data.id = this.$refs.UserForm.user.id;
                    }
                    break;
                case 'inline':
                    if (user_id !== '' && user_id !== undefined) {
                        data.id = user_id;
                    }
                    break;
            }

            if (data.id == '') {
                this.$notification.error({
                    message: this.$t('user.notifications.error.select_one'),
                });
            } else if ('ids' in data) {
                this.$http
                    .post('/api/users/reset-passwords', data)
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.$refs.UserTable.reload();

                            this.$notification.success({
                                message: response.data.message,
                            });
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVURP02' }),
                            });
                        }
                    });
            } else {
                this.$http
                    .post('/api/users/reset-password', data)
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.$refs.UserTable.reload();

                            this.$notification.success({
                                message: response.data.message,
                            });
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVURP01' }),
                            });
                        }
                    });
            }
        },
        resetExpiryDateModal(user_id = '') {
            let data = {
                ids: [],
            };

            if (user_id !== '' && user_id !== undefined) {
                data.ids = [user_id];
            } else if (this.selected_row_keys.length >= 1) {
                data.ids = this.selected_row_keys;
            }
            this.user_expiry_date = {
                expiry_date: null,
            };

            this.date_modal_visible = true;
        },
        resetExpiryDateModalAll() {
            this.user_expiry_date = {
                expiry_date: null,
            };

            this.date_modal_visible = true;
        },
        emailUser(data) {
            if (this.selected_row_keys.length >= 1) {
                data.ids = this.selected_row_keys;
            }

            let payload = {
                ids: [],
                ...data,
            };

            this.$http
                .post('/api/users/email', payload)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$notification.success({
                            message: response.data.message,
                        });
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVURLA03' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        resetAttempt(type, user_id = '') {
            let data = {
                ids: [],
            };

            switch (type) {
                case 'default':
                    if (this.selected_row_keys.length > 0) {
                        data.ids = this.selected_row_keys;
                    }
                    break;
                case 'form':
                    if (this.$refs.UserForm.user.id !== undefined) {
                        data.ids = [this.$refs.UserForm.user.id];
                    }
                    break;
                case 'inline':
                    if (user_id !== '' && user_id !== undefined) {
                        data.ids = [user_id];
                    }
                    break;
            }

            if (data.ids.length == 0) {
                this.$notification.error({
                    message: this.$t('user.notifications.error.select_one_or_more'),
                });
            } else {
                this.$http
                    .post('/api/users/reset-login-attempt', data)
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.$refs.UserTable.reload();

                            this.$notification.success({
                                message: response.data.message,
                            });
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVURLA01' }),
                            });
                        }
                    });
            }
        },
        handleModalCancel(event) {
            this.modal_visible = false;
        },
        handleDateModalCancel(event) {
            this.date_modal_visible = false;
        },
        handleModalOk(e) {
            this.$refs.UserPasswordForm.handleSubmit();
        },
        handleDateModalOk(e) {
            this.$refs.UserExpiryDateForm.handleSubmit();
        },
        isModalFormDirty(is_dirty) {
            this.modal_form_dirty = is_dirty;
        },
        onExpiryDateSave() {
            if (this.processing) {
                return false;
            }
            this.processing = true;

            if (this.$refs.UserTable.select_all_users) {
                let params = {
                    ...this.$refs.UserTable.params,
                    action: 'reset_expiry_date',
                    expiry_date: this.user_expiry_date.expiry_date.format('YYYY-MM-DD'),
                    select_all_users: this.$refs.UserTable.select_all_users,
                    company_id:
                        this.company_id != ''
                            ? this.company_id
                            : this.$refs.UserTable.params.company_id
                              ? this.$refs.UserTable.params.company_id
                              : null,
                    association_id:
                        this.association_id != ''
                            ? this.association_id
                            : this.$refs.UserTable.params.association_id
                              ? this.$refs.UserTable.params.association_id
                              : null,
                    search_value: this.$refs.UserTable.params.search_value
                        ? this.$refs.UserTable.params.search_value
                        : null,
                    active_users_only: this.$refs.UserTable.params.active_users_only
                        ? this.$refs.UserTable.params.active_users_only
                        : null,
                };

                this.$http
                    .post('/api/users/action-all-users', params)
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.$refs.UserTable.reload();
                            this.date_modal_visible = false;

                            this.$notification.success({
                                message: response.data.message,
                            });
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVURLA04' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            } else {
                let payload = {
                    ids: this.selected_row_keys,
                    action: 'reset_expiry_date',
                    expiry_date: this.user_expiry_date.expiry_date.format('YYYY-MM-DD'),
                };

                this.$http
                    .post('/api/users/reset-expiry-date', payload)
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.$refs.UserTable.reload();

                            this.date_modal_visible = false;

                            this.$notification.success({
                                message: response.data.message,
                            });
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVURLA02' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            }
        },
        onUserPasswordSave() {
            if (this.processing) {
                return false;
            }

            this.processing = true;

            let payload = {
                ...this.user_password,
            };

            this.$http
                .post('/api/users/change-password', payload)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$refs.UserTable.reload();

                        this.modal_form_dirty = false;
                        this.modal_visible = false;

                        this.$notification.success({
                            message: response.data.message,
                        });
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVUCP01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        onClose() {
            this.drawer_form_dirty = false;
            this.drawer_visible = false;
        },
        onSave(data) {
            if (this.processing) {
                return false;
            }

            this.drawer_form_dirty = false;
            this.processing = true;

            let id = this.user.id;

            const headers = { 'Content-Type': 'multipart/form-data' };

            if (id == undefined) {
                this.$http
                    .post('/api/users', data, { headers })
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.drawer_form_dirty = false;

                            this.$refs.UserTable.sorter = {
                                field: 'created_at',
                                order: 'descend',
                            };

                            this.$refs.UserTable.reload();

                            this.drawer_visible = false;
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 422) {
                            this.$notification.error({
                                message: error.response.data.message,
                            });

                            this.validation = error.response.data;
                        } else if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVUA01' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            } else {
                this.$http
                    .post('/api/users/' + id, data, { headers })
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.drawer_form_dirty = false;

                            this.$refs.UserTable.reload();
                            this.selected_row_keys = [];

                            this.drawer_visible = false;
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 422) {
                            this.$notification.error({
                                message: error.response.data.message,
                            });

                            this.validation = error.response.data;
                        } else if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVUU02' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            }
        },
        onSelectClick(event) {
            switch (event.key) {
                case 'ExportAdmin':
                    this.$refs.ExportDataModal.showModal();
                    break;
                case 'ExportAssociation':
                    this.$refs.ExportDataModal.showModal();
                    break;
                case 'ExportCompany':
                    this.onExport({
                        type: 'modal',
                        company_id: this.company_id,
                    });
                    break;
                case 'Import':
                    this.$refs.ImportDataModal.showModal();
                    break;
            }
        },
        onExport(data) {
            if (this.processing) {
                return false;
            }

            let params = {};

            if (data.type == 'selection') {
                params = {
                    type: 'selection',
                    ids: this.selected_row_keys.length >= 1 ? this.selected_row_keys : [],
                };
            } else {
                params = data;
            }

            this.processing = true;

            this.$http
                .post('/api/users/export', params)
                .then((response) => {
                    if (
                        _.includes(
                            [
                                'text/csv; charset=UTF-8',
                                'text/csv; charset=utf-8',
                                'text/plain; charset=UTF-8',
                                'text/plain; charset=utf-8',
                            ],
                            response.headers['content-type'],
                        )
                    ) {
                        this.$refs.ExportDataModal.hideModal();

                        this.$notification.success({ message: this.$t('user.notifications.success.export') });

                        FileManager.download(response);
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVUU03' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        onImport(data) {
            if (this.processing) {
                return false;
            }

            this.processing = true;
            const headers = { 'Content-Type': 'multipart/form-data' };

            this.$http
                .post('/api/users/import', data, { headers })
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$refs.ImportDataModal.fileList = [];
                        this.$refs.UserTable.reload();
                        this.$refs.ImportDataModal.hideModal();

                        this.$notification.success({ message: response.data.message });
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVUU03' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        reloadTable() {
            this.$refs.UserTable.reload();
        },
        updateEmailData(value) {
            this.email_data = {
                ...this.email_data,
                ...value,
            };
        },
    },
};
</script>
